import React, { useRef, useState, useEffect, useCallback } from 'react'
import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Mặt phẳng nghiêng + mặt phẳng ngang (Lớp 10 — Động lực học).
// Dốc nghiêng ở bên TRÁI (đỉnh trên-trái), vật trượt xuống SANG PHẢI, tới chân dốc thì TIẾP TỤC
// trượt trên mặt phẳng ngang (cùng hệ số ma sát μ) cho đến khi dừng.
// Trên dốc: hiện P, Pₜ, Pₙ (hình bình hành) + Fₘₛ. Trên mặt ngang: hiện P, N + Fₘₛ (cản chuyển động).
const G = 9.8
const W = 680, H = 400
const LPIX = 300, SLOPE_M = 3.75, SCALE = LPIX / SLOPE_M     // ≈ 80 px/m
const FOOT_X = 360, GROUND_Y = H - 56, HALF = 22
const GX_MAX = (W - 28 - (FOOT_X + HALF)) / SCALE            // quãng đường ngang tối đa khi KÉO ĐẶT (m)
const GX_OFF = (W + HALF * 2 - FOOT_X) / SCALE               // quãng đường để vật KHUẤT hẳn khỏi mép phải (m)

type Phase = 'incline' | 'ground'

const InclinedPlaneSimulation: React.FC<Props> = ({
  title = 'Mặt phẳng nghiêng — phân tích lực & ma sát',
  description = 'Dốc nghiêng bên trái: vật trượt xuống SANG PHẢI rồi tiếp tục trượt trên mặt phẳng ngang (cùng μ) đến khi dừng. Trên dốc hiện P→Pₜ,Pₙ (hình bình hành) + Fₘₛ; trên mặt ngang hiện P, N + Fₘₛ. Kéo đặt vật rồi thả tay.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [angle, setAngle] = useState(30)
  const [m, setM] = useState(2)
  const [mu, setMu] = useState(0.3)
  const [v0, setV0] = useState(0)
  const [playing, setPlaying] = useState(false)

  const phaseRef = useRef<Phase>('incline')
  const sRef = useRef(0.7)        // vị trí trên dốc (m, tính từ đỉnh)
  const gxRef = useRef(0)         // vị trí trên mặt ngang (m, từ chân dốc sang phải)
  const vRef = useRef(0)          // tốc độ (xuống dốc / sang phải)
  const aRef = useRef(0)
  const param = useRef({ angle, m, mu })
  const raf = useRef<number | null>(null)
  const lastTs = useRef<number | null>(null)
  const playRef = useRef(false)
  const dragRef = useRef(false)
  const firstRef = useRef(false)

  useEffect(() => { param.current = { angle, m, mu } }, [angle, m, mu])
  useEffect(() => { playRef.current = playing }, [playing])
  const fire = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const geom = (deg: number) => {
    const r = (deg * Math.PI) / 180
    const A = { x: FOOT_X, y: GROUND_Y }                                   // chân dốc (dưới-phải)
    const C = { x: FOOT_X - LPIX * Math.cos(r), y: GROUND_Y - LPIX * Math.sin(r) }  // đỉnh (trên-trái)
    const down = { x: Math.cos(r), y: Math.sin(r) }     // hướng XUỐNG dốc (sang phải-xuống)
    const nrm = { x: Math.sin(r), y: -Math.cos(r) }     // pháp tuyến ngoài (lên-phải)
    return { r, A, C, down, nrm }
  }
  const blockCenter = (deg: number) => {
    const { A, C, down, nrm } = geom(deg)
    if (phaseRef.current === 'incline') {
      const sp = Math.min(SLOPE_M, Math.max(0, sRef.current)) * SCALE
      return { x: C.x + down.x * sp + nrm.x * HALF, y: C.y + down.y * sp + nrm.y * HALF }
    }
    return { x: A.x + Math.max(0, gxRef.current) * SCALE, y: GROUND_Y - HALF }
  }

  const arrow = (ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number, color: string, label: string, dash = false) => {
    const len = Math.hypot(dx, dy); if (len < 1) return
    const ux = dx / len, uy = dy / len
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 3; ctx.setLineDash(dash ? [5, 4] : [])
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + dx, y + dy); ctx.stroke(); ctx.setLineDash([])
    const ah = 9
    ctx.beginPath(); ctx.moveTo(x + dx, y + dy)
    ctx.lineTo(x + dx - ah * ux - ah * 0.6 * uy, y + dy - ah * uy + ah * 0.6 * ux)
    ctx.lineTo(x + dx - ah * ux + ah * 0.6 * uy, y + dy - ah * uy - ah * 0.6 * ux)
    ctx.closePath(); ctx.fill()
    // nhãn + DẤU VEC-TƠ (gạch + mũi tên nhỏ trên đầu ký hiệu)
    const lx = x + dx + ux * 6 - 4, ly = y + dy + uy * 6 + 5
    ctx.font = 'bold 13px sans-serif'; ctx.fillStyle = color; ctx.fillText(label, lx, ly)
    const tw = ctx.measureText(label).width, ovy = ly - 12
    ctx.strokeStyle = color; ctx.lineWidth = 1.3
    ctx.beginPath(); ctx.moveTo(lx - 1, ovy); ctx.lineTo(lx + tw + 1, ovy); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(lx + tw + 1, ovy); ctx.lineTo(lx + tw - 3, ovy - 2.5); ctx.lineTo(lx + tw - 3, ovy + 2.5); ctx.closePath(); ctx.fill()
  }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    const { r, A, C, down, nrm } = geom(angle)
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#f8fafc'; ctx.fillRect(0, 0, W, H)
    ctx.fillStyle = '#e2e8f0'; ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y)   // mặt phẳng ngang

    // tam giác dốc: C (đỉnh trên-trái) – B (góc dưới-trái) – A (chân dưới-phải)
    ctx.beginPath(); ctx.moveTo(C.x, C.y); ctx.lineTo(C.x, GROUND_Y); ctx.lineTo(A.x, A.y); ctx.closePath()
    ctx.fillStyle = '#cbd5e1'; ctx.fill(); ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2; ctx.stroke()
    // cung + nhãn góc α tại chân dốc A
    ctx.strokeStyle = '#7c3aed'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(A.x, A.y, 40, Math.PI, Math.PI + r); ctx.stroke()
    ctx.fillStyle = '#6d28d9'; ctx.font = 'bold 14px sans-serif'; ctx.fillText(`α=${angle}°`, A.x - 82, A.y - 14)

    const bc = blockCenter(angle)
    const onInc = phaseRef.current === 'incline'
    const v = vRef.current

    // vật
    ctx.save(); ctx.translate(bc.x, bc.y); if (onInc) ctx.rotate(r)
    const g = ctx.createLinearGradient(0, -HALF, 0, HALF); g.addColorStop(0, '#fcd34d'); g.addColorStop(1, '#d97706')
    ctx.fillStyle = g; ctx.fillRect(-HALF, -HALF, HALF * 2, HALF * 2)
    ctx.strokeStyle = '#92400e'; ctx.lineWidth = 2; ctx.strokeRect(-HALF, -HALF, HALF * 2, HALF * 2)
    ctx.fillStyle = '#fff'; ctx.font = 'bold 12px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`${m}kg`, 0, 4); ctx.textAlign = 'left'
    ctx.restore()

    const P = m * G, vlen = 3   // px trên mỗi Newton — ĐỘ DÀI vec-tơ tỉ lệ với độ lớn lực (đổi m → P dài/ngắn theo)
    const tx = 16, ty = 24
    if (onInc) {
      const N = P * Math.cos(r), Pt = P * Math.sin(r), Fk = mu * N
      const tPt = { x: bc.x + down.x * Pt * vlen, y: bc.y + down.y * Pt * vlen }
      const tPn = { x: bc.x - nrm.x * N * vlen, y: bc.y - nrm.y * N * vlen }
      const tP = { x: bc.x, y: bc.y + P * vlen }
      ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4])
      ctx.beginPath(); ctx.moveTo(tPt.x, tPt.y); ctx.lineTo(tP.x, tP.y); ctx.lineTo(tPn.x, tPn.y); ctx.stroke(); ctx.setLineDash([])
      arrow(ctx, bc.x, bc.y, down.x * Pt * vlen, down.y * Pt * vlen, '#dc2626', 'Pₜ')
      arrow(ctx, bc.x, bc.y, -nrm.x * N * vlen, -nrm.y * N * vlen, '#0891b2', 'Pₙ')
      arrow(ctx, bc.x, bc.y, nrm.x * N * vlen, nrm.y * N * vlen, '#0d9488', 'N')   // phản lực pháp tuyến (ra khỏi mặt dốc)
      arrow(ctx, bc.x, bc.y, 0, P * vlen, '#b45309', 'P', true)
      let dir = 0, fm = 0
      if (Math.abs(v) > 0.02) { dir = -Math.sign(v); fm = Fk } else if (Pt > 0.01) { dir = -1; fm = Math.min(Fk, Pt) }
      if (dir !== 0) arrow(ctx, bc.x, bc.y, down.x * fm * vlen * dir, down.y * fm * vlen * dir, '#059669', 'Fₘₛ')
      ctx.font = 'bold 13px sans-serif'
      ctx.fillStyle = '#0f172a'; ctx.fillText(`P = m·g = ${P.toFixed(1)} N`, tx, ty)
      ctx.fillStyle = '#dc2626'; ctx.fillText(`Pₜ = P·sinα = ${Pt.toFixed(1)} N`, tx, ty + 20)
      ctx.fillStyle = '#0891b2'; ctx.fillText(`Pₙ = N = P·cosα = ${N.toFixed(1)} N`, tx, ty + 40)
      ctx.fillStyle = '#059669'; ctx.fillText(`Fₘₛ(max) = μ·N = ${Fk.toFixed(1)} N`, tx, ty + 60)
      ctx.font = 'bold 14px sans-serif'
      if (Math.abs(v) > 0.02) { ctx.fillStyle = '#7c3aed'; ctx.fillText(`v = ${v.toFixed(2)} m/s · a = ${aRef.current.toFixed(2)} m/s²`, tx, ty + 86) }
      else if (Pt > Fk) { ctx.fillStyle = '#dc2626'; ctx.fillText('Pₜ > Fₘₛ → THẢ TAY VẬT TRƯỢT XUỐNG (sang phải)', tx, ty + 86) }
      else { ctx.fillStyle = '#16a34a'; ctx.fillText('Pₜ ≤ Fₘₛ → VẬT ĐỨNG YÊN', tx, ty + 86) }
    } else {
      // ── MẶT PHẲNG NGANG: P (xuống) và N (lên) CÂN BẰNG; Fₘₛ cản chuyển động ──
      const N = P, Fk = mu * P
      arrow(ctx, bc.x, bc.y, 0, P * vlen, '#b45309', 'P')
      arrow(ctx, bc.x, bc.y, 0, -N * vlen, '#0891b2', 'N')
      if (Math.abs(v) > 0.02 && Fk > 0.01) arrow(ctx, bc.x, bc.y, -Math.sign(v) * Fk * vlen, 0, '#059669', 'Fₘₛ')
      ctx.font = 'bold 13px sans-serif'
      ctx.fillStyle = '#0f172a'; ctx.fillText(`Mặt phẳng ngang: N = P = ${P.toFixed(1)} N`, tx, ty)
      ctx.fillStyle = '#059669'; ctx.fillText(`Fₘₛ = μ·N = ${Fk.toFixed(1)} N (cản chuyển động)`, tx, ty + 20)
      ctx.font = 'bold 14px sans-serif'
      if (Math.abs(v) > 0.02) { ctx.fillStyle = '#7c3aed'; ctx.fillText(`v = ${v.toFixed(2)} m/s · a = ${aRef.current.toFixed(2)} m/s² (${Fk > 0.01 ? 'chậm dần' : 'đều — không ma sát'})`, tx, ty + 46) }
      else { ctx.fillStyle = '#16a34a'; ctx.fillText('Vật đã DỪNG (ma sát triệt tiêu vận tốc)', tx, ty + 46) }
    }
  }, [angle, m, mu])

  const loop = useCallback((ts: number) => {
    if (!playRef.current) return
    if (lastTs.current == null) lastTs.current = ts
    let dt = (ts - lastTs.current) / 1000; lastTs.current = ts; dt = Math.min(dt, 0.03)
    const { angle: aDeg, m: mm, mu: mmu } = param.current
    const r = (aDeg * Math.PI) / 180
    let v = vRef.current, a = 0, stop = false
    if (phaseRef.current === 'incline') {
      const Pt = mm * G * Math.sin(r), Fk = mmu * mm * G * Math.cos(r)
      if (Math.abs(v) < 0.02) { if (Pt > Fk) { a = (Pt - Fk) / mm; v = 0 } else { a = 0; v = 0; stop = true } }
      else a = (Pt - Fk * Math.sign(v)) / mm
      v += a * dt
      let s = sRef.current + v * dt
      if (s <= 0) { s = 0; v = 0; stop = true }
      if (s >= SLOPE_M) {              // tới chân dốc → chuyển sang mặt phẳng ngang
        sRef.current = SLOPE_M; phaseRef.current = 'ground'; gxRef.current = 0
        vRef.current = Math.max(0, v); aRef.current = a; draw()
        if (playRef.current) raf.current = requestAnimationFrame(loop); return
      }
      sRef.current = s; vRef.current = v; aRef.current = a
    } else {
      // mặt phẳng ngang: chỉ còn ma sát cản (N = mg). μ=0 → chạy ĐỀU tới khi KHUẤT khỏi màn hình.
      const Fk = mmu * mm * G
      if (Math.abs(v) < 0.02) { a = 0; v = 0; stop = true }
      else { a = -(Fk / mm) * Math.sign(v); const nv = v + a * dt; if (Math.sign(nv) !== Math.sign(v)) { v = 0; stop = true } else v = nv }
      const gx = gxRef.current + v * dt
      gxRef.current = gx; vRef.current = v; aRef.current = a
      if (gx >= GX_OFF) stop = true   // vật đã ra khỏi cửa sổ màn hình → kết thúc mô phỏng
    }
    draw()
    if (stop) { setPlaying(false); return }
    if (playRef.current) raf.current = requestAnimationFrame(loop)
  }, [draw])

  useEffect(() => { if (playing) { lastTs.current = null; raf.current = requestAnimationFrame(loop) } return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [playing, loop])
  useEffect(() => { if (!playing) draw() }, [draw, playing])

  // ── Kéo đặt vật: lên dốc (bên trái chân dốc) hoặc trên mặt phẳng ngang (bên phải) ──
  const toLogic = (e: React.PointerEvent) => { const rc = cv.current!.getBoundingClientRect(); return { x: (e.clientX - rc.left) / rc.width * W, y: (e.clientY - rc.top) / rc.height * H } }
  const onDown = (e: React.PointerEvent) => {
    const p = toLogic(e); const bc = blockCenter(angle)
    if (Math.hypot(p.x - bc.x, p.y - bc.y) < 42) { dragRef.current = true; setPlaying(false); fire(); (e.target as HTMLElement).setPointerCapture?.(e.pointerId) }
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const p = toLogic(e); const { A, C, down } = geom(angle)
    if (p.x <= A.x + 2) {
      phaseRef.current = 'incline'
      const sp = (p.x - C.x) * down.x + (p.y - C.y) * down.y
      sRef.current = Math.min(SLOPE_M, Math.max(0, sp / SCALE))
    } else {
      phaseRef.current = 'ground'
      gxRef.current = Math.min(GX_MAX, Math.max(0, (p.x - A.x) / SCALE))
    }
    vRef.current = 0; draw()
  }
  const onUp = () => { if (dragRef.current) { dragRef.current = false; vRef.current = v0; setPlaying(true) } }

  const reset = () => { setPlaying(false); phaseRef.current = 'incline'; sRef.current = 0.7; gxRef.current = 0; vRef.current = 0; aRef.current = 0; draw() }
  const run = () => { fire(); vRef.current = v0; setPlaying(true) }
  const sf = <T,>(s: (v: T) => void) => (v: T) => { fire(); s(v) }

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 my-3">
      <h4 className="font-bold text-violet-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab active:cursor-grabbing" />
      <div className="flex items-center justify-center gap-2 my-3">
        <button onClick={run} className="px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700">▶ Thả / Chạy</button>
        <button onClick={reset} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200">↺ Đặt lại lên dốc</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Góc α</span><input type="range" min={5} max={55} step={1} value={angle} onChange={(e) => sf(setAngle)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{angle}°</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Khối lượng m</span><input type="range" min={0.5} max={5} step={0.5} value={m} onChange={(e) => sf(setM)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{m} kg</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Hệ số ma sát μ</span><input type="range" min={0} max={1} step={0.05} value={mu} onChange={(e) => sf(setMu)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{mu.toFixed(2)}</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Vận tốc đầu v₀</span><input type="range" min={0} max={5} step={0.5} value={v0} onChange={(e) => sf(setV0)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{v0} m/s</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Kéo vật đặt trên dốc (hoặc trên mặt ngang bên phải) rồi thả tay. Vật trượt xuống dốc → tới chân dốc tiếp tục trượt trên mặt phẳng ngang, ma sát làm chậm dần rồi dừng.</p>
    </div>
  )
}

export default InclinedPlaneSimulation
